import { useLingui } from '@lingui/react/macro'

import { getProductItems } from '../helpers/getProductItems'
import { getEcommerce } from '../helpers/getEcommerce'

import type { TUseSubUsage } from '@/contexts/SiteSpecificContext/hooks/subUsage'
import type { ISubCreateProps } from '../type'

import { useAntdContext } from '@/contexts/AntdContext/hooks'
import { useAnalyticsData } from '@/hooks/analyticsData'
import { api } from '@/utils/api'
import { throwGlobalErrorMessage } from '@/utils/error'
import { useAppUIModal } from '@/contexts/AppUIModalContext'

export function useSubscriptionChange({
  onUpgradeSuccess,
}: {
  onUpgradeSuccess?: () => void
}) {
  const { t } = useLingui()

  const { message } = useAntdContext()

  const { paymentConfirm } = useAppUIModal()

  const { trackCustomEvent } = useAnalyticsData()

  const { mutateAsync, mutate, isLoading } = api.subLog.subUpgrade.useMutation({
    onSuccess: (data) => {},
    onError: () => {
      message.error(
        t`We were unable to process your payment. Please check the status of your payment method and try again.`,
      )
    },
    trpc: {
      context: {
        ignoreError: false,
      },
    },
  })

  /* 升级接口 */
  const subscriptionChange = async ({
    appName,
    pricesId,
    subscriptionId,
    userId,
    productPriceList,
    usage,
    platformName,
  }: ISubCreateProps & {
    subscriptionId: string
    usage?: TUseSubUsage
  }) => {
    paymentConfirm.show({
      onConfirm: async () => {
        const data = await mutateAsync({
          appName,
          pricesId,
          subscriptionId,
        })
        const { upgrade, timestamp } = data || {}
        if (upgrade && timestamp) {
          const items = getProductItems({
            productPriceList,
            appName,
          }).map((item) => {
            return {
              ...item,
              item_category5: subscriptionId,
            }
          })

          const { ecommerce } = getEcommerce({
            userId,
            productPriceList,
            items,
            appName,
            paymentPlatform: platformName,
          })
          // 升级上报
          const remainingUsage = usage?.remainingUsageMap
          if (remainingUsage && Object.keys(remainingUsage).length > 0) {
            trackCustomEvent({
              name: 'upgrade',
              meta: {
                ...ecommerce,
                remaining_usage: remainingUsage,
              },
            })
          } else {
            trackCustomEvent({
              name: 'upgrade',
              meta: ecommerce,
            })
          }
          onUpgradeSuccess?.()
        } else {
          // 升级失败
          throwGlobalErrorMessage(
            t`We were unable to process your payment. Please check the status of your payment method and try again.`,
          )
        }
        return {
          ok: !!upgrade,
        }
      },
    })
  }

  return {
    subscriptionChange,
    isLoading,
  }
}
